import { getBrowserSessionDiagnostics } from "@/lib/livekit-agent-telemetry";
import { buildActiveSessionBrowserEventLog } from "@/lib/livekit-browser-lifecycle-diagnostics";
import {
  type LiveKitSessionPresenceType,
  sendSessionControlShutdownMessage,
  sendSessionPresenceMessage,
} from "@/lib/livekit-session-shutdown";
import type { ConnectionState } from "livekit-client";
import { useEffect } from "react";

type RefLike<T> = {
  current: T;
};

type BrowserLifecycleLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
};

type LiveKitLifecycleDiagnosticsRoom = {
  name?: string;
  state: ConnectionState;
  localParticipant: {
    identity?: string;
    publishData: (
      data: Uint8Array,
      options: { reliable: true; topic: "session-control" }
    ) => Promise<void>;
  };
};

type UseLiveKitBrowserLifecycleDiagnosticsInput = {
  currentRoomRef: RefLike<LiveKitLifecycleDiagnosticsRoom>;
  isConnectedRef: RefLike<boolean>;
  isRecordingRef: RefLike<boolean>;
  logger: BrowserLifecycleLogger;
};

export function useLiveKitBrowserLifecycleDiagnostics({
  currentRoomRef,
  isConnectedRef,
  isRecordingRef,
  logger,
}: UseLiveKitBrowserLifecycleDiagnosticsInput) {
  useEffect(() => {
    const logActiveSessionBrowserEvent = (eventName: string, extra?: Record<string, unknown>) => {
      const payload = buildActiveSessionBrowserEventLog({
        eventName,
        isConnected: isConnectedRef.current,
        isRecording: isRecordingRef.current,
        room: currentRoomRef.current,
        browserDiagnostics: getBrowserSessionDiagnostics(),
        extra,
      });

      if (!payload) {
        return;
      }

      logger.warn("Browser lifecycle event during active LiveKit session", payload);
    };

    const sendPresence = (type: LiveKitSessionPresenceType, source: string) => {
      const activeRoom = currentRoomRef.current;
      if (!isConnectedRef.current) return;
      void sendSessionPresenceMessage({ activeRoom, type, source, logger });
    };

    const sendFinalDisconnect = (source: string) => {
      const activeRoom = currentRoomRef.current;
      if (!isConnectedRef.current) return;
      void sendSessionControlShutdownMessage({ activeRoom, source, logger });
    };

    const handleOnline = () => {
      logActiveSessionBrowserEvent("online");
      sendPresence("client_foregrounded", "online");
    };
    const handleOffline = () => {
      logActiveSessionBrowserEvent("offline");
      sendPresence("client_backgrounded", "offline");
    };
    const handleVisibilityChange = () => {
      logActiveSessionBrowserEvent("visibilitychange");
      sendPresence(
        document.visibilityState === "hidden" ? "client_backgrounded" : "client_foregrounded",
        "visibilitychange"
      );
    };
    const handlePageHide = (event: PageTransitionEvent) => {
      logActiveSessionBrowserEvent("pagehide", { persisted: event.persisted });
      if (event.persisted) {
        sendPresence("client_backgrounded", "pagehide_bfcache");
      } else {
        sendFinalDisconnect("pagehide");
      }
    };
    const handleBeforeUnload = () => {
      logActiveSessionBrowserEvent("beforeunload");
      sendFinalDisconnect("beforeunload");
    };
    const handleServiceWorkerControllerChange = () => {
      logActiveSessionBrowserEvent("serviceworker_controllerchange");
      sendFinalDisconnect("serviceworker_controllerchange");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);
    navigator.serviceWorker?.addEventListener(
      "controllerchange",
      handleServiceWorkerControllerChange
    );

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      navigator.serviceWorker?.removeEventListener(
        "controllerchange",
        handleServiceWorkerControllerChange
      );
    };
  }, [currentRoomRef, isConnectedRef, isRecordingRef, logger]);
}
